import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import api from '../../api';
import type { ObjectivePower } from './magnification';
import type { SpecimenKind, SpecimenSlide } from './specimens';

interface SlideLibraryAdminProps {
  slides: SpecimenSlide[];
  onSlidesChanged: () => Promise<void>;
}

const SlideLibraryAdmin: React.FC<SlideLibraryAdminProps> = ({ slides, onSlidesChanged }) => {
  const [open, setOpen] = useState(false);
  const [existingSlideId, setExistingSlideId] = useState('');
  const [slideId, setSlideId] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<SpecimenKind>('plant');
  const [group, setGroup] = useState<'plant' | 'animal' | 'microbe' | 'calibration'>('plant');
  const [stain, setStain] = useState('');
  const [structures, setStructures] = useState('');
  const [expectedObservation, setExpectedObservation] = useState('');
  const [creator, setCreator] = useState('');
  const [license, setLicense] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [objective, setObjective] = useState<ObjectivePower>(4);
  const [zPositions, setZPositions] = useState('-2, 0, 2');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const clearUpload = () => { setFiles([]); setError(''); setMessage(''); };
  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => setFiles(Array.from(event.target.files || []));
  const buildFormData = (planeField: 'planes' | 'metadata') => {
    const z = zPositions.split(',').map((value) => Number(value.trim()));
    if (!files.length || z.length !== files.length || z.some((value) => !Number.isFinite(value))) throw new Error('Select image files and provide one numeric z-position for each image.');
    const formData = new FormData();
    files.forEach((file) => formData.append('stackImages', file));
    const planes = z.map((zMicrometres, fileIndex) => ({ zMicrometres, fileIndex }));
    if (planeField === 'metadata') {
      formData.append('metadata', JSON.stringify({
        slideId: slideId.trim().toLowerCase(), name: name.trim(), kind, group, stain,
        structures: structures.split('\n').map((value) => value.trim()).filter(Boolean), expectedObservation: expectedObservation.trim(),
        imageAttribution: { creator: creator.trim(), license: license.trim(), sourceUrl: sourceUrl.trim() },
        focusStacks: { [objective]: planes },
      }));
    } else formData.append('planes', JSON.stringify(planes));
    return formData;
  };

  const createSlide = async () => {
    setError(''); setMessage(''); setBusy(true);
    try {
      const formData = buildFormData('metadata');
      await api.post('/microscopy/slides', formData);
      await onSlidesChanged();
      setMessage('Slide added. Upload other objective stacks by selecting it above.');
      setExistingSlideId(slideId.trim().toLowerCase()); setSlideId(''); setName(''); setStructures(''); setExpectedObservation(''); clearUpload();
    } catch (uploadError: any) { setError(uploadError?.response?.data?.message || uploadError.message || 'Could not add slide.'); }
    finally { setBusy(false); }
  };

  const appendStack = async () => {
    if (!existingSlideId) { setError('Choose an existing slide first.'); return; }
    setError(''); setMessage(''); setBusy(true);
    try {
      const formData = buildFormData('planes');
      await api.put(`/microscopy/slides/${encodeURIComponent(existingSlideId)}/focus-stacks/${objective}`, formData);
      await onSlidesChanged();
      setMessage(`Added ${files.length} focal plane(s) at ${objective}×.`);
      clearUpload();
    } catch (uploadError: any) { setError(uploadError?.response?.data?.message || uploadError.message || 'Could not add focus planes.'); }
    finally { setBusy(false); }
  };

  return <>
    <Button variant="outlined" size="small" onClick={() => setOpen(true)}>Manage slide library</Button>
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Microscopy slide library · Administrator</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">Upload browser-readable JPEG, PNG, or WebP focal planes. Include accurate creator, license, and source details for each new slide.</Typography>
          {error && <Alert severity="error">{error}</Alert>}{message && <Alert severity="success">{message}</Alert>}
          <Typography variant="h6">Add a new slide</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" label="Slug / slide ID" value={slideId} onChange={(event) => setSlideId(event.target.value)} />
            <TextField size="small" label="Display name" value={name} onChange={(event) => setName(event.target.value)} fullWidth />
            <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel id="micro-kind-label">Specimen type</InputLabel><Select labelId="micro-kind-label" label="Specimen type" value={kind} onChange={(event) => setKind(event.target.value as SpecimenKind)}>{['plant','animal','blood','leaf','stem','mitosis','bacteria','pond','yeast','muscle','nerve','calibration'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel id="micro-group-label">Group</InputLabel><Select labelId="micro-group-label" label="Group" value={group} onChange={(event) => setGroup(event.target.value as typeof group)}>{['plant','animal','microbe','calibration'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl>
            <TextField size="small" label="Stain" value={stain} onChange={(event) => setStain(event.target.value)} fullWidth />
            <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel id="micro-objective-label">Objective</InputLabel><Select labelId="micro-objective-label" label="Objective" value={objective} onChange={(event) => setObjective(event.target.value as ObjectivePower)}>{[4,10,40,100].map((power) => <MenuItem key={power} value={power}>{power}×</MenuItem>)}</Select></FormControl>
          </Stack>
          <TextField size="small" label="Structures (one per line)" multiline minRows={2} value={structures} onChange={(event) => setStructures(event.target.value)} />
          <TextField size="small" label="Expected observation" multiline minRows={2} value={expectedObservation} onChange={(event) => setExpectedObservation(event.target.value)} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}><TextField size="small" label="Image creator" value={creator} onChange={(event) => setCreator(event.target.value)} fullWidth /><TextField size="small" label="License (e.g. CC BY 4.0)" value={license} onChange={(event) => setLicense(event.target.value)} fullWidth /></Stack>
          <TextField size="small" label="Source URL (https)" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} />
          <TextField size="small" label="Focal plane z positions in µm (comma-separated, one per image)" value={zPositions} onChange={(event) => setZPositions(event.target.value)} />
          <Button component="label" variant="outlined">Choose focal-plane images<input hidden type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFiles} /></Button>
          <Typography variant="caption">{files.length ? `${files.length} files selected: ${files.map((file) => file.name).join(', ')}` : 'Select at least one focal-plane image.'}</Typography>
          <Button variant="contained" onClick={createSlide} disabled={busy || !slideId.trim() || !name.trim() || !expectedObservation.trim() || !creator.trim() || !license.trim() || !sourceUrl.trim()}>Publish slide and first focus stack</Button>
          <Typography variant="h6" sx={{ pt: 1 }}>Add another objective focus stack</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControl size="small" fullWidth><InputLabel id="existing-slide-label">Existing slide</InputLabel><Select labelId="existing-slide-label" label="Existing slide" value={existingSlideId} onChange={(event) => setExistingSlideId(event.target.value)}>{slides.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</Select></FormControl>
            <Button variant="contained" onClick={appendStack} disabled={busy || !existingSlideId || !files.length}>Append focus planes</Button>
          </Stack>
          <Typography variant="caption">Set the objective above, choose the stack images, and provide one z-position per file. The z positions are stored in micrometres.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  </>;
};

export default SlideLibraryAdmin;
